import React, { useEffect, useState } from 'react';

import IgAccountOptions from '../../IgAccountOptions';
import PlatformBadge from '../../PlatformBadge';
import apiClient from '../../../services/api';
import { AccountGroup, MaybePaginated, SocialAccount } from '../../../types/api';

interface AccountsStepProps {
  selected: number[];
  onChange: (next: number[]) => void;
  /** Instagram per-account options — mirrors the post form's toggles.
   * Collaborator: invited on the primary IG account's post instead of
   * feed-posting itself. Story: also publishes the media as a Story. */
  igCollaborators: number[];
  igStories: number[];
  onIgChange: (patch: { igCollaborators?: number[]; igStories?: number[] }) => void;
}

export default function AccountsStep({
  selected, onChange, igCollaborators, igStories, onIgChange,
}: AccountsStepProps) {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get<MaybePaginated<SocialAccount>>('/accounts/social-accounts/'),
      apiClient.get<MaybePaginated<AccountGroup>>('/accounts/groups/'),
    ]).then(([accData, grpData]) => {
      const rows = Array.isArray(accData) ? accData : accData.results || [];
      setAccounts(rows.filter((a) => a.is_active));
      const grps = Array.isArray(grpData) ? grpData : grpData.results || [];
      setGroups(grps);
    })
      .catch((e: Error) => setError(e.message || 'Failed to load accounts'))
      .finally(() => setLoading(false));
  }, []);

  function toggle(id: number): void {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  }

  function toggleGroup(group: AccountGroup): void {
    const groupAccountIds = group.accounts.filter((a) => a.is_active).map((a) => a.id);
    const allSelected = groupAccountIds.every((id) => selected.includes(id));
    const next = new Set(selected);
    if (allSelected) {
      groupAccountIds.forEach((id) => next.delete(id));
      setExpandedGroups((prev) => { const s = new Set(prev); s.delete(group.id); return s; });
    } else {
      groupAccountIds.forEach((id) => next.add(id));
      setExpandedGroups((prev) => new Set(prev).add(group.id));
    }
    onChange(Array.from(next));
  }

  function toggleAll(): void {
    if (selected.length === accounts.length) onChange([]);
    else onChange(accounts.map((a) => a.id));
  }

  const groupedAccountIds = new Set(groups.flatMap((g) => g.accounts.map((a) => a.id)));
  const ungrouped = accounts.filter((a) => !groupedAccountIds.has(a.id));

  const igSelectedCount = accounts.filter(
    (a) => a.platform === 'instagram' && selected.includes(a.id),
  ).length;

  function toggleIgList(key: 'igCollaborators' | 'igStories', id: number): void {
    const list = key === 'igCollaborators' ? igCollaborators : igStories;
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    onIgChange({ [key]: next });
  }

  /* Collaborator/Story toggles shown under any SELECTED Instagram
   * account row (both grouped and ungrouped renderings). */
  function igOptions(a: SocialAccount): React.ReactElement | null {
    return (
      <IgAccountOptions
        account={a}
        selected={selected.includes(a.id)}
        igSelectedCount={igSelectedCount}
        isCollaborator={igCollaborators.includes(a.id)}
        isStory={igStories.includes(a.id)}
        onToggleCollaborator={() => toggleIgList('igCollaborators', a.id)}
        onToggleStory={() => toggleIgList('igStories', a.id)}
      />
    );
  }

  return (
    <div className="wizard-accounts">
      {error && <div className="banner banner--error">{error}</div>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && accounts.length === 0 && (
        <div className="banner banner--info">
          No active accounts yet. Go to the Accounts tab to connect one,
          then come back here.
        </div>
      )}

      {accounts.length > 0 && (
        <>
          <div className="wizard-accounts-actions">
            <button type="button" className="ghost btn--sm" onClick={toggleAll}>
              {selected.length === accounts.length ? 'Clear all' : 'Select all'}
            </button>
            <span className="muted">
              {selected.length} of {accounts.length} selected
            </span>
          </div>

          <ul className="wizard-accounts-list">
            {groups.map((g) => {
              const groupAccounts = g.accounts.filter((a) => a.is_active);
              if (groupAccounts.length === 0) return null;
              const allOn = groupAccounts.every((a) => selected.includes(a.id));
              const someOn = groupAccounts.some((a) => selected.includes(a.id));
              const expanded = expandedGroups.has(g.id) || someOn;

              return (
                <li key={`g-${g.id}`} className="wizard-group">
                  <label className={`wizard-accounts-row wizard-group-header${allOn ? ' picked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                      onChange={() => toggleGroup(g)}
                    />
                    <span className="wizard-group-name">{g.name}</span>
                    <span className="muted wizard-group-count">{groupAccounts.length}</span>
                  </label>
                  {expanded && (
                    <ul className="wizard-group-accounts">
                      {groupAccounts.map((a) => (
                        <li key={a.id}>
                          <label className={`wizard-accounts-row${selected.includes(a.id) ? ' picked' : ''}`}>
                            <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} />
                            <PlatformBadge platform={a.platform} />
                            <span className="wizard-accounts-name">{a.account_name}</span>
                          </label>
                          {igOptions(a)}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}

            {ungrouped.map((a) => (
              <li key={a.id}>
                <label className={`wizard-accounts-row${selected.includes(a.id) ? ' picked' : ''}`}>
                  <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} />
                  <PlatformBadge platform={a.platform} />
                  <span className="wizard-accounts-name">{a.account_name}</span>
                </label>
                {igOptions(a)}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
